import { defineStore } from 'pinia'
import { ref, watch, computed } from 'vue'

export type ThemeMode = 'light' | 'dark' | 'auto'
export type ColorTheme = 'teal' | 'blue' | 'purple' | 'orange' | 'green'

interface ThemeState {
  mode: ThemeMode
  colorTheme: ColorTheme
}

export const useThemeStore = defineStore('theme', () => {
  // State
  const mode = ref<ThemeMode>((localStorage.getItem('theme-mode') as ThemeMode) || 'auto')
  const colorTheme = ref<ColorTheme>((localStorage.getItem('color-theme') as ColorTheme) || 'teal')

  // Computed
  const isDark = computed(() => {
    if (mode.value === 'auto') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
    }
    return mode.value === 'dark'
  })

  // Actions
  function setMode(newMode: ThemeMode) {
    mode.value = newMode
    localStorage.setItem('theme-mode', newMode)
    applyTheme()
  }

  function setColorTheme(newTheme: ColorTheme) {
    colorTheme.value = newTheme
    localStorage.setItem('color-theme', newTheme)
    applyColorTheme()
  }

  function applyTheme() {
    const root = document.documentElement
    if (isDark.value) {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }
  }

  function applyColorTheme() {
    const root = document.documentElement
    // Remove existing theme classes
    const themes: ColorTheme[] = ['teal', 'blue', 'purple', 'orange', 'green']
    themes.forEach((t) => root.classList.remove(`theme-${t}`))

    // Add new theme class
    root.classList.add(`theme-${colorTheme.value}`)

    // Update CSS variables dynamically if needed, or rely on class-based overrides in CSS
    updateCssVariables(colorTheme.value)
  }

  function updateCssVariables(theme: ColorTheme) {
    const root = document.documentElement
    const colors: Record<
      ColorTheme,
      { primary: string; primaryDark: string; primaryLight: string; secondary: string }
    > = {
      teal: {
        primary: '#14b8a6',
        primaryDark: '#0d9488',
        primaryLight: '#5eead4',
        secondary: '#3b82f6',
      },
      blue: {
        primary: '#3b82f6',
        primaryDark: '#2563eb',
        primaryLight: '#60a5fa',
        secondary: '#8b5cf6',
      },
      purple: {
        primary: '#8b5cf6',
        primaryDark: '#7c3aed',
        primaryLight: '#a78bfa',
        secondary: '#ec4899',
      },
      orange: {
        primary: '#f97316',
        primaryDark: '#ea580c',
        primaryLight: '#fdba74',
        secondary: '#ef4444',
      },
      green: {
        primary: '#22c55e',
        primaryDark: '#16a34a',
        primaryLight: '#86efac',
        secondary: '#06b6d4',
      },
    }

    const selected = colors[theme]
    root.style.setProperty('--color-primary', selected.primary)
    root.style.setProperty('--color-primary-dark', selected.primaryDark)
    root.style.setProperty('--color-primary-light', selected.primaryLight)
    root.style.setProperty('--color-secondary', selected.secondary)
  }

  // Initialize
  function init() {
    applyTheme()
    applyColorTheme()

    // Listen for system preference changes
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      if (mode.value === 'auto') {
        applyTheme()
      }
    })
  }

  return {
    mode,
    colorTheme,
    isDark,
    setMode,
    setColorTheme,
    init,
  }
})
